// The half of util.ts that runs anywhere, including Hermes.
//
// `randomToken` and `shortId` stay in util.ts because they call `globalThis.crypto`, which the
// native app's JS engine does not have. Splitting them out is what lets `@spark/shared/app`
// (src/app.ts) re-export these helpers without dragging a crypto call onto the phone; the app
// door's AST test fails if anything here grows a non-relative import.
//
// util.ts re-exports all of this, so every existing `from './util.ts'` import is unchanged.

export function slugify(text: string, max = 60): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/g, '')
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
}

/** "Hamza Khan" → "Hamza K." */
export function firstNameLastInitial(first: string, last: string): string {
  const f = first.trim()
  const l = last.trim()
  return l ? `${f} ${l[0]!.toUpperCase()}.` : f
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`
}

export function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

/** Stable string for comparing plain data (keys sorted). */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const maybeTs = value as { toMillis?: () => number }
  if (typeof maybeTs.toMillis === 'function') return `ts:${maybeTs.toMillis()}`
  // A Date has no own keys, so without this every Date would compare equal.
  if (value instanceof Date) return `date:${value.getTime()}`
  const obj = value as Record<string, unknown>
  return `{${Object.keys(obj)
    .sort()
    .filter((k) => obj[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`
}

/** Fills {{field}} merge tags. Unknown tags are left as-is so mistakes are visible in the preview. */
export function fillTemplate(template: string, values: Record<string, string>): string {
  // Own keys only, so {{constructor}} or {{toString}} is not filled from Object.prototype.
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (whole, key: string) => (Object.hasOwn(values, key) ? String(values[key]) : whole))
}

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)
}

/** Plain text → minimal HTML email body. */
export function textToHtml(text: string): string {
  return escapeHtml(text)
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('\n')
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  // A size below 1 would loop forever.
  if (!Number.isInteger(size) || size < 1) throw new RangeError(`chunk size must be a positive integer, got ${size}`)
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

export function byNumberThenText(a: string, b: string): number {
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' })
}

/** Removes undefined values so Firestore writes do not fail. */
export function compact<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T
}
